import React, { useEffect, useState, useMemo } from "react";
import { api, formatEUR } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, Calendar, FileDown, Info, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Switch } from "@/components/ui/switch";
import { Pagination } from "@/components/Pagination";
import { useStore } from "@/lib/storeContext";
import { getPageSize, setPageSize } from "@/lib/listDensity";

/**
 * Report Movimenti Gestionali — visibile solo al titolare (owner).
 * Elenca gli ordini chiusi come "preconto gestionale" (movimento non fiscale).
 */
export default function GestionaleReport() {
  const { user } = useAuth();
  const { storeId } = useStore();
  const isOwner = user?.role === "client_owner";
  const [items, setItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [histEnabled, setHistEnabled] = useState(true);
  const [armWipe, setArmWipe] = useState(false);

  // Filtro mese (default: mese corrente)
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(defaultMonth);
  const [page, setPage] = useState(1);
  const [pageSize, setSize] = useState(getPageSize);
  const [pages, setPages] = useState(1);

  const rangeOf = () => {
    const [y, m] = month.split("-");
    const from = `${y}-${m}-01T00:00:00`;
    const nextY = m === "12" ? Number(y) + 1 : Number(y);
    const nextM = m === "12" ? "01" : String(Number(m) + 1).padStart(2, "0");
    return { from, to: `${nextY}-${nextM}-01T00:00:00` };
  };

  const load = () => {
    setLoading(true);
    setError(null);
    const { from, to } = rangeOf();
    api
      .get(`/reports/management-movements?from_date=${encodeURIComponent(from)}&to_date=${encodeURIComponent(to)}&page=${page}&page_size=${pageSize}${storeId ? `&store_id=${storeId}` : ""}`)
      .then((r) => {
        setItems(Array.isArray(r.data.items) ? r.data.items : []);
        setTotalAmount(r.data.total || 0);
        setCount(r.data.count || 0);
        setPages(r.data.pages || 1);
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || "Errore nel caricamento");
        setItems([]); setTotalAmount(0); setCount(0); setPages(1);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { setPage(1); }, [month, storeId, pageSize]);
  useEffect(() => { load(); }, [month, page, storeId, pageSize]);
  useEffect(() => {
    api.get("/tenant/me").then((r) => setHistEnabled(r.data?.gestionale_history_enabled !== false));
  }, []);

  const toggleHistory = async (v) => {
    setHistEnabled(v);
    try {
      await api.patch("/tenant/branding", { gestionale_history_enabled: v });
      toast.success(v ? "Memorizzazione movimenti ATTIVA" : "Memorizzazione movimenti DISATTIVATA: i nuovi movimenti non verranno registrati");
    } catch (e) {
      setHistEnabled(!v);
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    }
  };

  const wipeHistory = async () => {
    if (!armWipe) {
      setArmWipe(true);
      setTimeout(() => setArmWipe(false), 4000);
      return;
    }
    setArmWipe(false);
    try {
      const r = await api.delete("/reports/management-movements");
      toast.success(`Storico eliminato: ${r.data.deleted} movimenti cancellati definitivamente`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  const groupedByDay = useMemo(() => {
    const g = {};
    for (const it of items) {
      const d = (it.closed_at || "").slice(0, 10);
      g[d] = g[d] || { total: 0, count: 0, items: [] };
      g[d].total += Number(it.total || 0);
      g[d].count += 1;
      g[d].items.push(it);
    }
    return Object.entries(g).sort(([a], [b]) => b.localeCompare(a));
  }, [items]);

  const downloadCSV = async () => {
    if (!count) { toast.info("Nessun movimento da esportare"); return; }
    // Esporta TUTTI i movimenti del mese, non solo la pagina corrente
    const { from, to } = rangeOf();
    let all = items;
    try {
      const r = await api.get(`/reports/management-movements?from_date=${encodeURIComponent(from)}&to_date=${encodeURIComponent(to)}&page=1&page_size=100${storeId ? `&store_id=${storeId}` : ""}`);
      all = r.data.items || [];
      for (let p = 2; p <= (r.data.pages || 1); p++) {
        const rp = await api.get(`/reports/management-movements?from_date=${encodeURIComponent(from)}&to_date=${encodeURIComponent(to)}&page=${p}&page_size=100${storeId ? `&store_id=${storeId}` : ""}`);
        all = all.concat(rp.data.items || []);
      }
    } catch { /* fallback: esporta la pagina corrente */ }
    const rows = [
      ["N. Movimento", "Data chiusura", "Modalità", "Tavolo", "Totale €", "Nota", "Operatore ID"],
      ...(Array.isArray(all) ? all : []).map((it) => [
        it.management_number || "",
        (it.closed_at || "").replace("T", " ").slice(0, 19),
        it.mode || "",
        it.table_name || "",
        Number(it.total || 0).toFixed(2),
        (it.management_note || "").replace(/[\n;]/g, " "),
        it.closed_by || "",
      ]),
    ];
    const csv = (Array.isArray(rows) ? rows : []).map((r) => (Array.isArray(r) ? r : []).map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `movimenti-gestionali-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8 lg:p-12 max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Report riservato titolare</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight flex items-center gap-3">
            <ShieldAlert className="w-8 h-8 text-velta-warning" /> Movimenti Gestionali
          </h1>
          <p className="text-velta-mute mt-2 max-w-2xl">
            Conti chiusi come <strong>Movimento Gestionale</strong> (gesto nascosto sulla scritta SCONTRINO in cassa). Non passano dalla stampante fiscale e non entrano nella chiusura giornaliera.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Mese</label>
            <input
              data-testid="gest-report-month"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="h-11 px-3 border border-velta-border rounded-md font-mono focus:border-[#1E2D5B] outline-none"
            />
          </div>
          <button
            data-testid="gest-report-export"
            onClick={downloadCSV}
            disabled={!count}
            className="h-11 px-4 bg-white border border-velta-border hover:border-velta-ink text-velta-ink rounded-md text-sm font-medium inline-flex items-center gap-2 btn-tactile disabled:opacity-40"
          >
            <FileDown className="w-4 h-4" /> Esporta CSV
          </button>
        </div>
      </div>

      {/* Impostazioni memorizzazione (solo titolare) */}
      {isOwner && (
        <div className="bg-white border border-velta-border rounded-md p-5 mb-8 flex items-center justify-between flex-wrap gap-4" data-testid="gest-settings-card">
          <div className="flex items-center gap-4">
            <Switch
              data-testid="gest-history-toggle"
              checked={histEnabled}
              onCheckedChange={toggleHistory}
            />
            <div>
              <div className="font-medium text-sm">Memorizza e mostra i movimenti gestionali</div>
              <div className="text-xs text-velta-mute max-w-md">
                {histEnabled
                  ? "I movimenti vengono registrati e visibili in questo report e in Documenti del giorno."
                  : "DISATTIVATO: i nuovi movimenti vengono stampati ma NON registrati da nessuna parte."}
              </div>
            </div>
          </div>
          <button
            data-testid="gest-wipe-btn"
            onClick={wipeHistory}
            className={`h-11 px-4 rounded-md text-sm font-medium inline-flex items-center gap-2 btn-tactile border-2 ${
              armWipe ? "bg-red-600 border-red-600 text-white animate-pulse" : "bg-white border-red-300 text-red-600 hover:bg-red-50"
            }`}
          >
            <Trash2 className="w-4 h-4" />
            {armWipe ? "Confermi? Eliminazione definitiva" : "Elimina storico movimenti"}
          </button>
        </div>
      )}

      {/* Totali */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border border-velta-border rounded-md p-5">
          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute">Movimenti nel mese</div>
          <div data-testid="gest-report-count" className="font-heading text-4xl font-bold mt-2">{count}</div>
        </div>
        <div className="bg-white border-2 border-velta-warning/40 rounded-md p-5">
          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute">Totale importi</div>
          <div data-testid="gest-report-total" className="font-heading text-4xl font-bold mt-2 tabular-nums">{formatEUR(totalAmount)}</div>
        </div>
        <div className="bg-white border border-velta-border rounded-md p-5">
          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute">Media per movimento</div>
          <div className="font-heading text-4xl font-bold mt-2 tabular-nums">
            {count > 0 ? formatEUR(totalAmount / count) : formatEUR(0)}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6 text-sm text-red-900 flex items-start gap-3">
          <Info className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <strong>Errore:</strong> {error}
            {error.toLowerCase().includes("solo il titolare") && (
              <div className="mt-1 text-xs text-red-800">Questa pagina è visibile solo all&apos;account owner del tenant.</div>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-velta-mute py-16 text-sm">Caricamento…</div>
      ) : items.length === 0 && !error ? (
        <div className="bg-white border border-dashed border-velta-border rounded-md p-12 text-center">
          <ShieldAlert className="w-10 h-10 text-velta-mute mx-auto mb-3" />
          <div className="font-heading font-semibold text-lg mb-1">Nessun movimento gestionale in questo mese</div>
          <div className="text-sm text-velta-mute max-w-md mx-auto">
            Per registrare un movimento gestionale: <strong>tieni premuto 2,5 secondi</strong> (touch) o fai <strong>doppio click</strong> (PC) sulla scritta <strong>SCONTRINO</strong> sopra il tastierino in cassa.
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {(Array.isArray(groupedByDay) ? groupedByDay : []).map(([day, grp]) => (
            <div key={day} className="bg-white border border-velta-border rounded-md overflow-x-auto pos-scroll">
              <div className="px-5 py-3 bg-velta-bg border-b border-velta-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-velta-mute" />
                  <span className="font-heading font-semibold">{day}</span>
                  <span className="text-xs font-mono text-velta-mute">· {grp.count} movimento{grp.count > 1 ? "i" : ""}</span>
                </div>
                <div className="font-mono font-bold text-lg tabular-nums">{formatEUR(grp.total)}</div>
              </div>
              <table className="w-full text-sm min-w-[720px]">
                <thead className="text-left text-xs font-mono uppercase tracking-widest text-velta-mute">
                  <tr>
                    <th className="px-4 py-2">N.</th>
                    <th className="px-4 py-2">Orario</th>
                    <th className="px-4 py-2">Tavolo</th>
                    <th className="px-4 py-2">Modalità</th>
                    <th className="px-4 py-2">Nota</th>
                    <th className="px-4 py-2 text-right">Importo</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(grp.items) ? grp.items : []).map((it) => (
                    <tr key={it.id} className="border-t border-velta-border/50 hover:bg-velta-bg/30">
                      <td className="px-4 py-2 font-mono">#{it.management_number || "—"}</td>
                      <td className="px-4 py-2 font-mono text-xs">{(it.closed_at || "").slice(11, 19)}</td>
                      <td className="px-4 py-2 text-xs font-medium">{it.table_name || <span className="opacity-40">—</span>}</td>
                      <td className="px-4 py-2 uppercase text-xs tracking-widest font-mono text-velta-mute">{it.mode || "—"}</td>
                      <td className="px-4 py-2 text-velta-mute">{it.management_note || <span className="opacity-50">—</span>}</td>
                      <td className="px-4 py-2 font-mono font-bold text-right tabular-nums">{formatEUR(it.total || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          <Pagination
            page={page}
            pages={pages}
            total={count}
            onPage={setPage}
            pageSize={pageSize}
            onPageSize={(n) => { setSize(setPageSize(n)); setPage(1); }}
          />
        </div>
      )}
    </div>
  );
}
